export interface ExternalLink {
  id: string;
  title: string;
  url: string;
  iconUrl: string;
}

import { useQuery } from "react-query";

import { getUseQueryLocalStorage, setUseQueryLocalStorage } from "@/utils";
import { AppProperties } from "@/api";

const fetchFile = async (url: string) => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch file`);
    }
    return await response.text();
  } catch (error) {
    throw new Error(
      error instanceof Error ? error.message : "Failed to fetch file",
    );
  }
};

const QUERY_KEY = "apppropertyfiles";

const FILE_KEYS = ["WebExternalLinks"];

type AppPropertyFile = Record<string, unknown>;

interface AppPropertyFiles {
  [key: string]: AppPropertyFile;
}

export const useAppPropertyFiles = (
  properties?: AppProperties,
  props?: {
    refetchOnMount: "always" | boolean;
  },
) => {
  const { refetchOnMount } = props ?? {};

  return useQuery<AppPropertyFiles>(
    [QUERY_KEY],
    async () => {
      const parsedFiles: AppPropertyFiles = {};
      try {
        await Promise.all(
          FILE_KEYS.map(async (key) => {
            const url = properties?.[key];
            if (url) {
              const file = await fetchFile(url);
              try {
                parsedFiles[key] = JSON.parse(file);
              } catch (error) {
                console.error(`Failed to parse JSON for ${key}:`, error);
                throw new Error(`Invalid JSON format in ${key} file`);
              }
            }
          }),
        );
      } catch (error) {
        console.error("Failed to fetch app property files:", error);
      }
      return parsedFiles;
    },
    {
      enabled: !!properties,
      onSuccess: (data) => {
        setUseQueryLocalStorage(QUERY_KEY, data);
      },
      initialData: () => {
        return (
          (getUseQueryLocalStorage(QUERY_KEY) as AppPropertyFiles) ?? undefined
        );
      },
      refetchOnMount,
    },
  );
};
